import { useState } from "react";

export interface FieldsMap {
	[key: string]: any;
}

export interface FormValidationResult {
	valid: boolean;
	errors?: { [key: string]: string };
}

export interface FormOptions {
	validateOnChange?: boolean;
	initialState?: FieldsMap;
}

export interface FormCustomValidator {
	validate: (fields: FieldsMap) => FormValidationResult;
}

export interface FormFieldsValidator {
	[key: string]: (field: string) => FormValidationResult;
}

export type FormValidator = FormFieldsValidator | FormCustomValidator;

function validateForm(
	fields: FieldsMap,
	validator?: FormValidator
): FormValidationResult {
	let customValidation = { valid: true } as any;

	if (!validator || Object.keys(validator).length === 0) {
		return { valid: true };
	}

	if ("validate" in validator) {
		customValidation = (validator as FormCustomValidator).validate(fields);
	}
	const fieldsValidation = Object.keys(validator)
		.filter((k) => k !== "validate")
		.reduce(
			(acc: any, k) => {
				const validationResult = validator[k](fields[k]);

				return {
					...acc,
					valid: validationResult.valid && acc.valid,
					...(validationResult.message && {
						errors: {
							...acc.errors,
							[k]: validationResult.message,
						},
					}),
				};
			},
			{ valid: true }
		);

	return {
		valid: customValidation.valid && fieldsValidation.valid,
		...((customValidation.errors || fieldsValidation.errors) && {
			errors: {
				...customValidation.errors,
				...fieldsValidation.errors,
			},
		}),
	};
}

const useForm = (validator?: FormValidator, options?: FormOptions) => {
	const [fieldsMap, setFieldsMap] = useState<FieldsMap>(
		options?.initialState || {}
	);
	const [validationResult, setValidationResult] = useState<any>({
		valid: true,
		errors: {},
	});

	const validateOnChange = options?.validateOnChange;

	return {
		handleUpdatesBatch: (fieldNames) => (values) => {
			const updatedFields = {
				...fieldsMap,
				...fieldNames.reduce(
					(acc, f, idx) => ({
						...acc,
						[f]: values[idx],
					}),
					{}
				),
			};

			setFieldsMap(updatedFields);

			if (validateOnChange) {
				const updatedValidation = validateForm(updatedFields, validator);

				setValidationResult(updatedValidation);
			}
		},
		handleUpdates: (fieldName) => (value) => {
			const updatedFields = {
				...fieldsMap,
				[fieldName]: value ?? null,
			};

			setFieldsMap(updatedFields);

			if (validateOnChange) {
				const updatedValidation = validateForm(updatedFields, validator);

				setValidationResult(updatedValidation);
			}
		},
		handleSubmit: (handler) => () => handler(fieldsMap),
		validate: () => {
			const result = validateForm(fieldsMap, validator);

			setValidationResult(result);
			return result;
		},
		initialize: (fields: FieldsMap) => setFieldsMap(fields),
		formState: {
			state: fieldsMap,
		},
		valid: validationResult.valid,
		errors: validationResult.errors,
	};
};

export default useForm;
